import { FC } from 'react';
import { useLocale } from '@app/core/hooks/useLocale';
import styles from '../../opm-summary-waterfall-erf.module.scss';
import classNames from 'classnames';
import {
  OpmSampleWaterfallsDto,
  OpmSampleWaterfallInstrumentDto,
} from '@app/shared/models/contracts/opm-calculation-results-dto';
import SvgChevron from '@app/shared/icons/Chevron';
import { ExpandableKeys, ExpandedSectionsState } from './SampleWaterfallOutputsSummary';
import { NOT_APPLICABLE, WaterfallCellValue } from './sample-waterfall-column-helpers';
import WaterfallValueCell from './WaterfallValueCell';

interface ShareholderDebtSectionProps {
  waterfallResults: Dictionary<OpmSampleWaterfallsDto>;
  columnKeys: string[];
  shareholderDebtInstruments: (OpmSampleWaterfallInstrumentDto & { instrumentId: string })[];
  onToggle: (key: string) => void;
  expandedSections: ExpandedSectionsState;
}

const ShareholderDebtSection: FC<ShareholderDebtSectionProps> = ({
  waterfallResults,
  columnKeys,
  shareholderDebtInstruments,
  onToggle,
  expandedSections,
}): JSX.Element => {
  const { l } = useLocale();

  const getAggregateValue = (
    columnKey: string,
    valueKey: 'totalDebtInstrumentCarryingValue' | 'totalShareholderDebtDistributions' | 'shortfall'
  ): WaterfallCellValue =>
    waterfallResults[columnKey] ? waterfallResults[columnKey][valueKey] : NOT_APPLICABLE;

  const getInstrumentValue = (
    columnKey: string,
    instrumentId: string,
    valueKey: 'carryingValue' | 'totalDistributions'
  ): WaterfallCellValue => {
    const instrument = waterfallResults[columnKey]?.instruments?.[instrumentId];
    return instrument ? instrument[valueKey] : NOT_APPLICABLE;
  };

  return (
    <>
      <tr>
        <th className={styles['section-header']} colSpan={columnKeys.length + 1}>
          {!expandedSections.shareholderDebt ? (
            <div className={styles['expand-toggle']}>
              <SvgChevron
                onClick={() => onToggle(ExpandableKeys.ShareholderDebt)}
                className={styles[`chevron-collapsed`]}
              />
            </div>
          ) : (
            <div className={styles['expand-toggle']}>
              <SvgChevron
                onClick={() => onToggle(ExpandableKeys.ShareholderDebt)}
                className={styles[`chevron-expanded`]}
              />
            </div>
          )}
          {l('_ShareholderDebtContractualCarryingValuesTitle')}
        </th>
      </tr>
      {expandedSections.shareholderDebt && (
        <>
          {shareholderDebtInstruments.map((instrument) => (
            <tr key={instrument.instrumentId}>
              <td>{instrument.instrumentName}</td>
              {columnKeys.map((key) => (
                <WaterfallValueCell
                  key={key}
                  value={getInstrumentValue(key, instrument.instrumentId, 'carryingValue')}
                />
              ))}
            </tr>
          ))}
          <tr>
            <th className={classNames(styles['bold-text'], styles['border-cell-top'])}>
              {l('_TotalContractualShareholderDebt')}
            </th>
            {columnKeys.map((key) => (
              <WaterfallValueCell
                key={key}
                value={getAggregateValue(key, 'totalDebtInstrumentCarryingValue')}
                className={classNames(styles['bold-text'], styles['border-cell-top'])}
              />
            ))}
          </tr>
          <tr>
            <th className={styles['section-header']} colSpan={columnKeys.length + 1}>
              {l('_ShareholderDebtDistributionsTitle')}
            </th>
          </tr>
          {shareholderDebtInstruments.map((instrument) => (
            <tr key={instrument.instrumentId}>
              <td>{instrument.instrumentName}</td>
              {columnKeys.map((key) => (
                <WaterfallValueCell
                  key={key}
                  value={getInstrumentValue(key, instrument.instrumentId, 'totalDistributions')}
                />
              ))}
            </tr>
          ))}
          <tr>
            <th className={classNames(styles['bold-text'], styles['border-cell-top'])}>
              {l('_TotalShareholderDebtDistributions')}
            </th>
            {columnKeys.map((key) => (
              <WaterfallValueCell
                key={key}
                value={getAggregateValue(key, 'totalShareholderDebtDistributions')}
                className={classNames(styles['bold-text'], styles['border-cell-top'])}
              />
            ))}
          </tr>
          <tr>
            <th>{l('_Shortfall')}</th>
            {columnKeys.map((key) => (
              <WaterfallValueCell key={key} value={getAggregateValue(key, 'shortfall')} />
            ))}
          </tr>
        </>
      )}
    </>
  );
};

export default ShareholderDebtSection;
